import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import esLocale from '@fullcalendar/core/locales/es';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import DropdownFilter from 'components/common/DropdownFilter';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import React, { useContext, useEffect, useRef, useState } from 'react';

import {
    Alert,
    Button,
    Card,
    Col,
 OverlayTrigger,
    Row,
    Tooltip
} from 'react-bootstrap';
import AddPublicacionModal from './AddPublicacionModal';
import './CalendarioEditorial.css';
import PublicacionEventModal from './PublicacionEventModal';

const CalendarioEditorial = () => {
    const {
        config: { isRTL, isDark }
    } = useContext(AppContext);
    const { token } = useToken();
    const calendarRef = useRef();
    const [title, setTitle] = useState('');
    const [calendarApi, setCalendarApi] = useState(null);
    const [currentFilter, setCurrentFilter] = useState('Month View');
    const [isOpenPublicacionModal, setIsOpenPublicacionModal] = useState(false);
    const [isOpenEventModal, setIsOpenEventModal] = useState(false);
    const [modalEventContent, setModalEventContent] = useState({});
    const [selectedDate, setSelectedDate] = useState();
    const [publicaciones, setPublicaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mensaje, setMensaje] = useState('');
    const [tipoMensaje, setTipoMensaje] = useState('success');
    const [cuentaComercialActual, setCuentaComercialActual] = useState('');
    const [cuentasComerciales, setCuentasComerciales] = useState([]);
    const [cuentasComercialesData, setCuentasComercialesData] = useState([]);
    const [selectedCuentaId, setSelectedCuentaId] = useState(null);

    const viewName = [
        'Month View',
        'Week View',
        'Day View',
        'List View'
    ];

    const handleCompanyFilter = company => {
        setCuentaComercialActual(company);
        const selected = cuentasComercialesData.find(c => c.vDescripcion === company);
        if (selected) {
            setSelectedCuentaId(selected.idCatCuentaComercial);
            // Pass the ID directly to avoid state update delay
            cargarPublicaciones(selected.idCatCuentaComercial);
        }
    };

    const eventTimeFormat = {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        meridiem: true
    };

    const cargarPublicaciones = async (cuentaId = null) => {
        setLoading(true);
        try {
            // Use the passed parameter or fall back to state
            const idToUse = cuentaId !== null ? cuentaId : selectedCuentaId;

            let url = '/api/calendarioeditorial/obtenerpublicaciones';
            if (idToUse) {
                url += `?idCatCuentaComercial=${idToUse}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const publicacionesData = detalles.table1 || [];

                const publicacionesAgrupadas = {};
                publicacionesData.forEach(pub => {
                    const fecha = pub.dFechaPublicacion.split('T')[0];
                    if (!publicacionesAgrupadas[fecha]) {
                        publicacionesAgrupadas[fecha] = [];
                    }

                    let redesSociales = [];
                    try {
                        redesSociales = JSON.parse(pub.vRedesSociales || '[]');
                    } catch (e) {
                        console.error('Error parseando redes sociales:', e);
                    }

                    publicacionesAgrupadas[fecha].push({
                        idPublicacion: pub.idPublicacion,
                        descripcion: pub.vDescripcion,
                        redesSociales: redesSociales,
                        nombreUsuario: pub.vResponsable || pub.vNombreUsuario || 'Usuario',
                        estado: pub.vEstado || 'Planeado',
                        tema: pub.vTema || '',
                        formato: pub.vNombreFormato || null,
                        llamadaAccion: pub.vNombreLlamadaAccion || null,
                        idCatFormatoPublicacion: pub.idCatFormatoPublicacion || 0,
                        idCatLlamadaAccion: pub.idCatLlamadaAccion || 0,
                        idCatEstadoPublicacion: pub.idCatEstadoPublicacion || 1
                    });
                });

                const eventos = Object.keys(publicacionesAgrupadas).map(fecha => {
                    const pubsDelDia = publicacionesAgrupadas[fecha];
                    
                    // Crear eventos individuales para cada publicación
                    const eventosPublicaciones = pubsDelDia.map((pub, index) => {
                        let backgroundColor = '#009bdb';
                        
                        if (pub.estado === 'Publicado') {
                            backgroundColor = '#89bd00';
                        } else if (pub.estado === 'Programado') {
                            backgroundColor = '#fc9646';
                        }

                        return {
                            id: `${fecha}-${pub.idPublicacion}`,
                            title: pub.tema || pub.descripcion,
                            start: fecha,
                            allDay: true,
                            backgroundColor: backgroundColor,
                            borderColor: backgroundColor,
                            order: index,
                            extendedProps: {
                                publicacion: pub,
                                redesSociales: pub.redesSociales || [],
                                estado: pub.estado,
                                nombreUsuario: pub.nombreUsuario,
                                tipoEvento: 'publicacion',
                                tema: pub.tema,
                                formato: pub.formato,
                                llamadaAccion: pub.llamadaAccion
                            }
                        };
                    });

                    // Recopilar todas las redes sociales únicas del día
                    const redesSocialesUnicas = new Set();
                    pubsDelDia.forEach(pub => {
                        if (pub.redesSociales && Array.isArray(pub.redesSociales)) {
                            pub.redesSociales.forEach(red => redesSocialesUnicas.add(red));
                        }
                    });

                    // Crear un evento para las redes sociales si hay alguna
                    const eventosRedes = [];
                    if (redesSocialesUnicas.size > 0) {
                        eventosRedes.push({
                            id: `${fecha}-redes`,
                            title: 'Redes Sociales',
                            start: fecha,
                            allDay: true,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            display: 'auto',
                            order: 9999,
                            classNames: ['evento-redes-sociales'],
                            extendedProps: {
                                redesSociales: Array.from(redesSocialesUnicas),
                                tipoEvento: 'redes'
                            }
                        });
                    }

                    return [...eventosPublicaciones, ...eventosRedes];
                }).flat(); // Aplanar el array de arrays

                setPublicaciones(eventos);
            } else {
                setPublicaciones([]);
            }
        } catch (error) {
            console.error('Error al cargar publicaciones:', error);
            mostrarMensaje('Error al cargar las publicaciones', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const cargarCompanias = async () => {
        try {
            const response = await fetch('/api/calendarioeditorial/obtenercuentascomerciales', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const cuentasComercialesData = detalles.cuentasComerciales || [];
                
                setCuentasComercialesData(cuentasComercialesData);
                const nombres = cuentasComercialesData.map(c => c.vDescripcion);
                setCuentasComerciales(nombres);
                if (nombres.length > 0) {
                    setCuentaComercialActual(nombres[0]);
                    setSelectedCuentaId(cuentasComercialesData[0].idCatCuentaComercial);
                    // Load publications for the first account
                    cargarPublicaciones(cuentasComercialesData[0].idCatCuentaComercial);
                }
            } else {
                console.log('Error al cargar cuentas comerciales:');
            }
        } catch (error) {
            console.error('Error al cargar cuentas comerciales:', error);
          
        }
    };

    const mostrarMensaje = (texto, tipo = 'success') => {
        setMensaje(texto);
        setTipoMensaje(tipo);
        setTimeout(() => {
            setMensaje('');
        }, 4000);
    };

    const handleEventClick = info => {
        // No hacer nada si es un evento de redes sociales
        if (info.event.extendedProps.tipoEvento === 'redes') {
            return;
        }

        if (info.event.url) {
            window.open(info.event.url);
            info.jsEvent.preventDefault();
        } else {
            setModalEventContent(info);
            setIsOpenEventModal(true);
        }
    };

    const handleFilter = filter => {
        if (!calendarApi) return;

        setCurrentFilter(filter);

        switch (filter) {
            case 'Month View':
                calendarApi.changeView('dayGridMonth');
                break;
            case 'Week View':
                calendarApi.changeView('timeGridWeek');
                break;
            case 'Day View':
                calendarApi.changeView('timeGridDay');
                break;
            case 'List View':
                calendarApi.changeView('listWeek');
                break;
            default:
                calendarApi.changeView('dayGridMonth');
        }
    };

    useEffect(() => {
        cargarPublicaciones();
        cargarCompanias();
    }, []);

    useEffect(() => {
        if (calendarRef.current) {
            const api = calendarRef.current.getApi();
            setCalendarApi(api);
            setTitle(api.view.title);
        }
    }, [calendarRef.current]);

    const handlePublicacionAdded = (mensaje) => {
        mostrarMensaje(mensaje);
        cargarPublicaciones();
    };

    const handleEstadoActualizado = () => {
        cargarPublicaciones();
    };

    return (
        <div className="calendario-editorial-wrapper">
            <Card >
                <Card.Header>
                    <Row className="align-items-center gx-0 ">
                        <Col xs="auto" className="d-flex justify-content-end order-md-1">
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip style={{ position: 'fixed' }} id="prevTooltip">
                                        Anterior
                                    </Tooltip>
                                }
                            >
                                <Button
                                    variant="link"
                                    className={`icon-item icon-item-sm icon-item-hover shadow-none p-0 me-1 ms-md-2 ${isDark ? 'text-white' : ''}`}
                                    onClick={() => {
                                        if (calendarApi) {
                                            calendarApi.prev();
                                        }
                                    }}
                                    disabled={!calendarApi}
                                >
                                    <FontAwesomeIcon icon="arrow-left" />
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip style={{ position: 'fixed' }} id="nextTooltip">
                                        Siguiente
                                    </Tooltip>
                                }
                            >
                                <Button
                                    variant="link"
                                    className={`icon-item icon-item-sm icon-item-hover shadow-none p-0 me-lg-2 ${isDark ? 'text-white' : ''}`}

                                    onClick={() => {
                                        if (calendarApi) {
                                            calendarApi.next();
                                        }
                                    }}
                                    disabled={!calendarApi}
                                >
                                    <FontAwesomeIcon icon="arrow-right" />
                                </Button>
                            </OverlayTrigger>
                        </Col>
                        <Col xs="auto" className="d-flex justify-content-end order-md-2">
                            <h4 className={`mb-0 fs-0 fs-sm-1 fs-lg-2 ${isDark ? 'text-white' : ''}`}>
                                {title}
                            </h4>
                        </Col>
                        <Col xs md="auto" className="d-flex justify-content-end order-md-3">

                            <Button
                                size="sm"
                                className="me-2"
                                variant="falcon-primary"
                                onClick={() => {
                                    if (calendarApi) {
                                        calendarApi.today();
                                    }
                                }}
                                disabled={!calendarApi}
                            >
                                Hoy
                            </Button>
                            <DropdownFilter
                               
                                filters={cuentasComerciales}
                                currentFilter={cuentaComercialActual}
                                handleFilter={handleCompanyFilter}
                                icon="building"
                                right
                            />
                        </Col>

                        <Col md="auto" className="d-md-none">
                            <hr />
                        </Col>

                        <Col className="d-flex justify-content-end order-md-2">
                            <DropdownFilter
                                className="me-2"
                                filters={viewName}
                                currentFilter={currentFilter}
                                handleFilter={handleFilter}
                                icon="sort"
                                right
                            />
                        </Col>
                    </Row>

                    {mensaje && (
                        <Row className="mt-3">
                            <Col>
                                <Alert variant={tipoMensaje} dismissible onClose={() => setMensaje('')}>
                                    {mensaje}
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Row className="mt-2">
                        <Col>
                            <div className="d-flex align-items-center gap-3 text-muted small">
                                <div className="d-flex align-items-center gap-1">
                                    <div style={{ width: '12px', height: '12px', backgroundColor: '#009bdb', borderRadius: '2px' }}></div>
                                    <span>Planeado</span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <div style={{ width: '12px', height: '12px', backgroundColor: '#fc9646', borderRadius: '2px' }}></div>
                                    <span>Programado</span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <div style={{ width: '12px', height: '12px', backgroundColor: '#89bd00', borderRadius: '2px' }}></div>
                                    <span>Publicado</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body className={`p-0 ${isDark ? 'bg-dark' : ''}`}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className={`mt-2 ${isDark ? 'text-white-50' : 'text-muted'}`}>Cargando publicaciones...</p>
                        </div>
                    ) : (
                        <div className={isDark ? 'calendar-dark-mode' : ''}>
                            <FullCalendar
                                ref={calendarRef}
                                headerToolbar={false}
                                plugins={[
                                    dayGridPlugin,
                                    timeGridPlugin,
                                    interactionPlugin,
                                    listPlugin
                                ]}
                                initialView="dayGridMonth"
                                themeSystem="bootstrap"
                                dayMaxEvents={false}
                                direction={isRTL ? 'rtl' : 'ltr'}
                                height={800}
                                stickyHeaderDates={false}
                                locale={esLocale}
                                editable={false}
                                selectable
                                selectMirror
                                eventOrder="order,start,title"
                                select={info => {
                                    setIsOpenPublicacionModal(true);
                                    setSelectedDate(info.start);
                                }}
                                eventTimeFormat={eventTimeFormat}
                                eventClick={handleEventClick}
                                events={publicaciones}
                                datesSet={(dateInfo) => {
                                    setTitle(dateInfo.view.title);
                                }}
                                eventContent={(arg) => {
                                    const { tipoEvento, redesSociales } = arg.event.extendedProps;

                                    // Si es un evento de redes sociales
                                    if (tipoEvento === 'redes') {
                                        const redesInfoMap = {
                                            'facebook': { icon: ['fab', 'facebook'] },
                                            'instagram': { icon: ['fab', 'instagram'] },
                                            'x': { icon: ['fab', 'x-twitter'] },
                                            'tiktok': { icon: ['fab', 'tiktok'] },
                                            'youtube': { icon: ['fab', 'youtube'] },
                                            'linkedin': { icon: ['fab', 'linkedin'] },
                                            'threads': { icon: ['fab', 'threads'] }
                                        };

                                        return (
                                            <div className="fc-event-main-frame" style={{ 
                                                width: '100%', 
                                                padding: '4px',
                                                backgroundColor: 'transparent',
                                                border: 'none'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '3px',
                                                    flexWrap: 'wrap',
                                                    justifyContent: 'center'
                                                }}>
                                                    {redesSociales && redesSociales.map((redId, iconIndex) => {
                                                        const redInfo = redesInfoMap[redId];
                                                        return redInfo ? (
                                                            <div
                                                                key={iconIndex}
                                                                style={{
                                                                    backgroundColor: '#009bdb',
                                                                    borderRadius: '3px',
                                                                    padding: '3px 6px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '24px',
                                                                    height: '20px',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                                                }}
                                                                title={redId.charAt(0).toUpperCase() + redId.slice(1)}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={redInfo.icon}
                                                                    style={{
                                                                        fontSize: '0.7rem',
                                                                        color: 'white'
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    }

                                    // Si es un evento de publicación
                                    return (
                                        <div className="fc-event-main-frame" style={{ width: '100%', padding: '2px 4px' }}>
                                            <div className="fc-event-title-container" style={{ width: '100%' }}>
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    lineHeight: '1.2'
                                                }}>
                                                    <span style={{ 
                                                        color: 'white',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block'
                                                    }}>
                                                        {arg.event.title}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>

            <AddPublicacionModal
                isOpenModal={isOpenPublicacionModal}
                setIsOpenModal={setIsOpenPublicacionModal}
                publicaciones={publicaciones}
                setPublicaciones={setPublicaciones}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedCuentaId={selectedCuentaId}
                onPublicacionAdded={handlePublicacionAdded}
            />

            <PublicacionEventModal
                isOpenEventModal={isOpenEventModal}
                setIsOpenEventModal={setIsOpenEventModal}
                modalEventContent={modalEventContent}
                onEstadoActualizado={handleEstadoActualizado}
                selectedCuentaId={selectedCuentaId}
            />
        </div>
    );
};

export default CalendarioEditorial;

